import { useRef } from 'react'

import {
  PDF_VIEWER_ALL_TOOLS,
  PdfViewerFitMode,
} from '../../PdfViewer.constants'
import { checkIsPdfViewerPageScrollMode } from '../../PdfViewer.utils'
import { usePdfViewerPan } from '../usePdfViewerPan'
import { usePdfViewerPanels } from '../usePdfViewerPanels'
import { usePdfViewerScale } from '../usePdfViewerScale'
import { usePdfViewerSearch } from '../usePdfViewerSearch'
import { usePdfViewerViewMode } from '../usePdfViewerViewMode'

import { usePdfViewerDocument } from './usePdfViewerDocument'
import { usePdfViewerInstance } from './usePdfViewerInstance'
import { usePdfViewerPages } from './usePdfViewerPages'

export function usePdfViewer({
  defaultOpenPanels,
  file,
  fitMode = PdfViewerFitMode.PageWidth,
  search,
  setPdfViewerInstance,
  singlePage = false,
  tools = PDF_VIEWER_ALL_TOOLS,
  viewerRootRef,
}) {
  const rootRef = useRef(null)
  const containerRef = useRef(null)
  const viewerElementRef = useRef(null)
  const selectAreaLayerRef = useRef(null)
  const pdfViewerRef = useRef(null)
  const eventBusRef = useRef(null)
  const findControllerRef = useRef(null)

  const {
    fitPagesToContainer,
    isPageFitMode: isPageFitScale,
    publishPageWidth,
    resetScale,
    scaleProps,
    onScaleChanging,
  } = usePdfViewerScale({
    containerRef,
    fitMode,
    pdfViewerRef,
    viewerRootRef: rootRef,
  })

  const { isPageScrollMode, resetViewMode, rotation, viewModeProps } =
    usePdfViewerViewMode({ pdfViewerRef, singlePage })

  const {
    closeThumbnailsOnNarrowScreen,
    isSearchOpen,
    isThumbnailsOpen,
    panelProps,
  } = usePdfViewerPanels({ defaultOpenPanels, tools })

  const {
    activePage,
    navigationProps,
    resetPages,
    onPageChanging,
    onPagesInit,
    onSelectThumbnailPage,
  } = usePdfViewerPages({
    pdfViewerRef,
    onSelectThumbnailPage: closeThumbnailsOnNarrowScreen,
  })

  function handleReplaceDocument() {
    resetPages()
    resetScale()
    resetViewMode()
  }

  const {
    hasDocumentLoadError,
    isDocumentLoading,
    pdfDocument,
    onPageRendered,
  } = usePdfViewerDocument({
    file,
    pdfViewerRef,
    onReplaceDocument: handleReplaceDocument,
  })

  const searchProps = usePdfViewerSearch({
    eventBusRef,
    findControllerRef,
    pdfDocument,
    search,
  })

  const { isPanMode, isPanning, onTogglePanMode } = usePdfViewerPan({
    containerRef,
  })

  function handlePagesInit(pdfViewer) {
    fitPagesToContainer()
    onPagesInit(pdfViewer)
  }

  // pdf.js keeps the scale measured for the page that was current when the
  // fit ran, so a differently sized page would come out unfitted
  function handlePageChanging(event, pdfViewer) {
    onPageChanging(event)

    if (checkIsPdfViewerPageScrollMode(pdfViewer.scrollMode)) {
      fitPagesToContainer()
    }

    publishPageWidth()
  }

  usePdfViewerInstance({
    containerRef,
    eventBusRef,
    findControllerRef,
    pdfViewerRef,
    rootRef,
    selectAreaLayerRef,
    setPdfViewerInstance,
    singlePage,
    viewerElementRef,
    viewerRootRef,
    onPageChanging: handlePageChanging,
    onPageRendered,
    onPagesInit: handlePagesInit,
    onScaleChanging,
  })

  return {
    containerRef,
    hasDocumentLoadError,
    isDocumentLoading,
    isPageFitMode: isPageFitScale && isPageScrollMode,
    isPanMode,
    isPanning,
    isSearchOpen,
    isThumbnailsOpen,
    navigationProps,
    pdfDocument,
    rootRef,
    searchProps,
    selectAreaLayerRef,
    thumbnailsProps: {
      activePage,
      rotation,
      onSelectPage: onSelectThumbnailPage,
    },
    toolbarProps: {
      ...panelProps,
      ...scaleProps,
      ...viewModeProps,
      isPanMode,
      isSearchOpen,
      isThumbnailsOpen,
      tools,
      onTogglePanMode,
    },
    viewerElementRef,
  }
}
